import * as api from 'api';
import { actionTypes } from 'redux-resource';
import { SubmissionError } from 'redux-form';
import { PER_PAGE } from 'helpers/actions';
import { expireTypeToBackendValue } from '../helpers/subs';
import { BIG_PAGE_SIZE } from '../constants';

export const fetchAllSubs =
  (page = 1, ordering) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'subs',
      requestKey: `fetchAll${page}`,
      list: `page${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchAllSubs(page, ordering)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const fetchAllUserSubs = (userId) => async (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: `fetchAllUserSubs${userId}`,
    list: `userSubs${userId}`,
    mergeListIds: false,
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  let subs = [];
  let page = 1;
  let next = 'next-url';

  while (next) {
    let response;

    try {
      response = await api.searchSubs(
        page,
        { user_profile: userId },
        BIG_PAGE_SIZE,
      );
    } catch (e) {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
      let messages = e.response
        ? e.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError({
        _error: messages,
      });
    }

    subs = subs.concat(response.data.results);
    next = response.data.next;
    page += 1;
  }

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_SUCCEEDED,
    resources: subs || [],
  });

  return subs;
};

export const fetchUserSubs =
  (userId, page = 1) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'subs',
      requestKey: `fetchByUser${page}`,
      list: 'userSubs',
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserSubs(userId, page)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const fetchUserFilteredSubs =
  (userId, { query, requestKey }, page = 1) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'subs',
      requestKey: `${requestKey}Subs${page}`,
      list: `${requestKey}Subs${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserFilteredSubs(userId, query, page)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const fetchAllInitialsSubs =
  (params, { sort, carriers, expiry_type, status = 'expired' }) =>
  async (dispatch) => {
    const actionDefaults = {
      resourceType: 'subs',
      requestKey: `fetchAllInitialsSubs`,
      list: `allInitialsSubs`,
      mergeListIds: false,
    };

    const apiCalls = {
      sprint: api.fetchAllInitialsSubs,
      att: api.fetchAllAttInitialsSubs,
      tracfone: api.fetchAllTracfoneInitialsSubs,
      simo: api.fetchAllSimoInitialsSubs,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      resources: [],
    });

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    let subs = [];

    for (let carrier of carriers) {
      let page = 1;
      let next = 'next-url';

      while (next) {
        let response;

        try {
          response = await apiCalls[carrier](
            page,
            { ...params, [`${carrier}_status`]: status },
            BIG_PAGE_SIZE,
          );
        } catch (e) {
          let messages = e.response
            ? e.response.data
            : { detail: 'Something went wrong' };
          dispatch({
            ...actionDefaults,
            type: actionTypes.READ_RESOURCES_FAILED,
          });
          throw new SubmissionError({
            _error: messages,
          });
        }

        subs = subs.concat(response.data.results);
        next = response.data.next;
        page += 1;
      }
    }

    // sort subs if needed
    subs = typeof sort === 'function' ? sort(subs) : subs;

    // filter out subs with wrong expiry type
    subs = subs.filter(
      (sub) =>
        sub.sprint_status !== 'expired' ||
        sub.expiry_type === expireTypeToBackendValue(expiry_type),
    );

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      resources: subs,
    });

    return subs;
  };

export const findSub = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: 'find',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findSub(subId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        statusCode: err.response && err.response.status,
      });
    });
};

export const updateSub = (id, sub) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: 'updateSub',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateSub(id, sub)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const fetchUpcomingPlan = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: 'all',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .fetchSubUpcomingPlan(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [id],
        meta: {
          upcomingPlan: response.data.results,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const portModify = (subId, port) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: `portModify${subId}`,
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .portModify(subId, port)
    .then((response) => {
      const { subscription, ...requestProperties } = response.data;
      const subMeta = {};
      subMeta[subId] = {
        portInfo: requestProperties.port_info,
      };
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        requestProperties,
      });
      dispatch({
        type: 'UPDATE_RESOURCES',
        meta: {
          subs: subMeta,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: err.response && err.response.data,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updatePort = (subId, port) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: `updatePort${subId}`,
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updatePort(subId, port)
    .then((response) => {
      const { subscription, ...requestProperties } = response.data;
      const subMeta = {};
      subMeta[subId] = {
        portInfo: requestProperties.port_info,
      };
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        requestProperties,
      });
      dispatch({
        type: 'UPDATE_RESOURCES',
        meta: {
          subs: subMeta,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: err.response && err.response.data,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const portCancel = (subId, cancel) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    resources: [subId],
    requestKey: 'portCancel',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .portCancel(subId, cancel)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data.subscription],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const portInfo = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    resources: [id],
    requestKey: `portInfo${id}`,
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .portInfo(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        requestProperties: {
          data: response.data.current,
        },
        meta: {
          portInfo: response.data.current,
          allPortInfo: response.data,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const searchSubs =
  (page = 1, search) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'subs',
      requestKey: `searchSubs${page}`, // this is used elsewhere so beware when changing
      list: `searchPage${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .searchSubs(page, search)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const findSubDataUsage = (subId, cycle) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: 'dataUsage',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findSubDataUsage(subId, cycle)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        meta: {
          dataUsage: response.data,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const findSubVoiceUsage = (subId, cycle) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: 'voiceUsage',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findSubVoiceUsage(subId, cycle)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        meta: {
          voiceUsage: response.data,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const findSubRoamingUsage = (subId, cycle) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: 'roamingUsage',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findSubRoamingUsage(subId, cycle)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        meta: {
          roamingUsage: response.data,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const findSubDailyUsage =
  (subId, cycle = 'current') =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'subs',
      requestKey: 'dailyUsage',
      resources: [subId],
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .findSubDailyUsage(subId, cycle)
      .then((response) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          meta: {
            dailyUsage: response.data,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const activateSub = (subId, activation) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'subs',
    requestKey: 'activateSub',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .activateSub(subId, activation)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        meta: {
          activation: {
            ...response.data,
            pin: activation.pin,
          },
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const startSwitchSprintToAtt = (id, payload) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'csa',
    requestKey: 'switchSprintToAtt',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .switchSprintToAtt(id, payload)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      return error;
    });
};

export const confirmSwitchSprintToAtt = (id, payload) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'csa',
    requestKey: 'switchSprintToAtt',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .confirmSwitchSprintToAtt(id, payload)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      return error;
    });
};
